import { Capacitor } from "@capacitor/core";
import { isImage, processAttachment } from "@msys/ui";
import {
  Add as AddIcon,
  AddAPhoto as AddAPhotoIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FieldArray, useFormikContext } from "formik";
import { findIndex, isNil } from "lodash-es";
import React from "react";
import { useDrag, useDrop } from "react-dnd";
import { useLatest } from "react-use";
import { v4 } from "uuid";
import * as Yup from "yup";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { FormattedFloatOptionalField } from "../../../commons/form-fields/FormattedFloatOptionalField.js";
import { FormikError } from "../../../commons/form-fields/FormikError.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { LocalDndProvider } from "../../../dnd.js";
import {
  AttachmentPhotoUploaderWithEditor,
  AttachmentPhotoUploaderWithEditorRef,
} from "../../attachments/AttachmentPhotoUploaderWithEditor.js";
import { AttachmentUploader } from "../../attachments/AttachmentUploader.js";
import { getRotatedUrl } from "../../attachments/helpers.js";
import { PropertyGalleryBox } from "../boxes/PropertyGalleryBox.js";
import { AllowedValue, PropValues } from "../properties.js";

export interface AllowedValuesFormValues {
  defineAllowedValues: boolean;
  allowedValues: AllowedValue[];
}

interface Props {
  type: "TEXT" | "NUMBER";
  disabled?: boolean;
}

export const AllowedValuesField = (props: Props) => {
  return (
    <LocalDndProvider>
      <AllowedValuesFieldComponent {...props} />
    </LocalDndProvider>
  );
};

const AllowedValuesFieldComponent = ({ type, disabled }: Props) => {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const formikProps = useFormikContext<AllowedValuesFormValues>();

  const [, drop] = useDrop({ accept: "field" });
  const [draggedValueKey, setDraggedValueKey] = React.useState<string | null>(
    null
  );

  return (
    <>
      <CheckboxField
        name={"defineAllowedValues"}
        label={t("Define allowed values", {
          ns: "QuoteItem",
        })}
        disabled={disabled}
      />
      {formikProps.values.defineAllowedValues && (
        <FieldArray
          name="allowedValues"
          render={arrayHelpers => (
            <Stack
              direction="column"
              spacing={1}
              sx={{
                alignSelf: "stretch",
                paddingLeft: 4,
              }}
            >
              <Stack
                direction="column"
                spacing={1}
                ref={drop}
                sx={{
                  alignSelf: "stretch",
                }}
              >
                {formikProps.values.allowedValues.map((value, index) => (
                  <AllowedValueField
                    type={type}
                    name="allowedValues"
                    values={formikProps.values.allowedValues}
                    value={value}
                    key={`value-${value.key}`}
                    index={index}
                    disabled={disabled}
                    canRemove={formikProps.values.allowedValues.length >= 1}
                    draggedValueKey={draggedValueKey}
                    setDraggedValueKey={setDraggedValueKey}
                  />
                ))}
              </Stack>
              <FormikError formikProps={formikProps} name="allowedValues" />
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="text"
                color="secondary"
                sx={{ alignSelf: "flex-start" }}
                onClick={() => {
                  arrayHelpers.push({ value: null, media: null, key: v4() });
                }}
                disabled={disabled}
              >
                {t("Add value", { ns: "Global" })}
              </Button>
            </Stack>
          )}
        />
      )}
    </>
  );
};

function AllowedValueField({
  type,
  name,
  value,
  values,
  index,
  disabled,
  canRemove,
  draggedValueKey,
  setDraggedValueKey,
}: {
  type: "TEXT" | "NUMBER";
  name: string;
  value: AllowedValue;
  values: AllowedValue[];
  index: number;
  disabled?: boolean;
  canRemove: boolean;
  draggedValueKey: string | null;
  setDraggedValueKey: React.Dispatch<React.SetStateAction<string | null>>;
}) {
  const { t } = useTranslate(["QuoteItem", "Global"]);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const formikProps = useFormikContext<AllowedValuesFormValues>();

  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  const attachmentPhotoUploaderRef =
    React.useRef<AttachmentPhotoUploaderWithEditorRef>(null);

  const stateRef = useLatest({
    formikProps,
    draggedValueKey,
    setDraggedValueKey,
  });

  const [, drop] = useDrop<
    { valueKey: string; type: string },
    void,
    { isDragging: boolean }
  >({
    accept: "field",
    canDrop: () => false,
    hover({ valueKey: draggedKey }, monitor) {
      if (stateRef.current.draggedValueKey !== draggedKey) {
        stateRef.current.setDraggedValueKey(draggedKey);
      }
      if (draggedKey !== value.key) {
        const dragIndex = findIndex(values, v => v.key === draggedKey);
        const hoverIndex = findIndex(values, v => v.key === value.key);
        const newValues = [...values];
        const draggedValue = newValues.splice(dragIndex, 1)[0];
        newValues.splice(hoverIndex, 0, draggedValue);
        stateRef.current.formikProps.setFieldValue(name, newValues);
      }
    },
  });
  const [{ isDragging }, drag, dragPreview] = useDrag<
    { valueKey: string; type: string },
    void,
    { isDragging: boolean }
  >({
    item: { type: "field", valueKey: value.key },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    end: (dropResult, monitor) => {
      stateRef.current.setDraggedValueKey(null);
      // const didDrop = monitor.didDrop();
    },
  });

  return (
    <Stack
      direction="row"
      spacing={1}
      key={index}
      ref={node => dragPreview(node as HTMLDivElement | null)}
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        opacity: isDragging ? 0.25 : 1,
        userSelect: "none",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          flexGrow: 1,
          flexShrink: 1,
        }}
      >
        <Box
          ref={node => drop(drag(node as HTMLDivElement | null))}
          sx={{
            display: "flex",
            height: "48px",
            alignItems: "center",
            cursor: "move",
          }}
        >
          <DragIndicatorIcon color="action" />
        </Box>

        {value.media && (
          <PropertyGalleryBox
            pictures={[value.media].map(processAttachment).filter(isImage)}
            objectFit="contain"
            imageHeight={60}
            notInStack
            sx={{
              flexGrow: 0,
              flexShrink: 0,
              width: "80px",
              height: "60px",
              aspectRatio: "80 / 60",
            }}
            handleRotate={(attachment, direction) => {
              formikProps.setFieldValue(
                `${name}.${index}.media.url`,
                getRotatedUrl(attachment.url, direction)
              );
            }}
            handleDelete={() => {
              formikProps.setFieldValue(`${name}.${index}.media`, null);
            }}
          />
        )}
        {type === "NUMBER" ? (
          <FormattedFloatOptionalField
            name={`${name}.${index}.value`}
            label={t("Value", {
              ns: "Global",
            })}
            disabled={disabled}
            autoFocus={!draggedValueKey} // doesn't work well with dragging
          />
        ) : (
          <TextField
            name={`${name}.${index}.value`}
            label={t("Value", {
              ns: "Global",
            })}
            disabled={disabled}
            autoFocus={!draggedValueKey} // doesn't work well with dragging
          />
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: "center",
        }}
      >
        {!value.media && (
          <>
            <IconButton
              size="small"
              color="secondary"
              disabled={disabled || isSubmitting}
              onClick={async () => {
                if (disabled) return;

                if (Capacitor.isNativePlatform()) {
                  attachmentPhotoUploaderRef.current?.start();
                } else {
                  attachmentInputRef.current?.click();
                }
              }}
            >
              {isSubmitting ? (
                <CircularProgress color="inherit" size={24} />
              ) : (
                <AddAPhotoIcon />
              )}
            </IconButton>
            {Capacitor.isNativePlatform() && (
              <AttachmentPhotoUploaderWithEditor
                ref={attachmentPhotoUploaderRef}
                onStart={() => {
                  setIsSubmitting(true);
                }}
                onAttachment={attachment => {
                  if (attachment) {
                    formikProps.setFieldValue(
                      `${name}.${index}.media`,
                      attachment
                    );
                  }
                  setIsSubmitting(false);
                }}
              />
            )}
            <AttachmentUploader
              innerRef={attachmentInputRef}
              accept={"image/*,.heic,.heif"}
              multiple={false}
              onStart={() => {
                setIsSubmitting(true);
              }}
              onComplete={attachment => {
                if (attachment) {
                  formikProps.setFieldValue(
                    `${name}.${index}.media`,
                    attachment
                  );
                }
                setIsSubmitting(false);
              }}
            />
          </>
        )}
        <IconButton
          size="small"
          color="primary"
          disabled={!canRemove || disabled}
          onClick={() => {
            formikProps.setFieldValue(
              name,
              values.filter(v => v !== value)
            );
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
}

export const useAllowedValuesFieldValidationSchema = (
  property: PropValues | null
) => {
  const { t } = useTranslate(["QuoteItem", "Global"]);

  return React.useMemo(
    () => ({
      defineAllowedValues: Yup.boolean().required(),
      allowedValues: Yup.array().when("defineAllowedValues", {
        is: true,
        then: Yup.array()
          .when("type", {
            is: "NUMBER",
            then: Yup.array()
              .of(
                Yup.object().shape({
                  value: Yup.number()
                    .nullable()
                    .required()
                    .label(
                      t("Value", {
                        ns: "Global",
                      })
                    )
                    .test(
                      "unique",
                      t("Value must be unique", {
                        ns: "Global",
                      }),
                      function (value: number | null | undefined) {
                        // @ts-ignore
                        const allowedValues = (this.from as any)[1].value
                          .allowedValues as { value: number | null }[];
                        const sameValues = allowedValues.filter(
                          v => v.value === value
                        );
                        return sameValues.length <= 1;
                      }
                    ),
                  media: Yup.object().nullable(),
                })
              )
              .min(1)
              .test({
                test: value => {
                  if (property) {
                    if (!isNil(value) && value.length > 0) {
                      if (
                        property.isNumber &&
                        !property.isArray &&
                        !isNil(property.valueNumber)
                      ) {
                        return value.some(
                          allowedValue =>
                            allowedValue.value === property.valueNumber
                        );
                      } else if (
                        property.isNumber &&
                        property.isArray &&
                        !isNil(property.valueNumberArray)
                      ) {
                        return property.valueNumberArray.every(valueNumber =>
                          value.some(
                            allowedValue => allowedValue.value === valueNumber
                          )
                        );
                      }
                    }
                  }

                  return true;
                },
                message: t("Allowed values must contain current value", {
                  ns: "QuoteItem",
                  value:
                    property && property.isNumber && property.isArray ? 2 : 1,
                }),
              }),
          })
          .when("type", {
            is: "TEXT",
            then: Yup.array()
              .of(
                Yup.object().shape({
                  value: Yup.string()
                    .nullable()
                    .required()
                    .label(
                      t("Value", {
                        ns: "Global",
                      })
                    )
                    .test(
                      "unique",
                      t("Value must be unique", {
                        ns: "Global",
                      }),
                      function (value: string | null | undefined) {
                        // @ts-ignore
                        const allowedValues = (this.from as any)[1].value
                          .allowedValues as { value: string | null }[];
                        const sameValues = allowedValues.filter(
                          v => v.value === value
                        );
                        return sameValues.length <= 1;
                      }
                    ),
                  media: Yup.object().nullable(),
                })
              )
              .min(1)
              .test({
                test: value => {
                  if (property) {
                    if (!isNil(value) && value.length > 0) {
                      if (
                        property.isText &&
                        !property.isArray &&
                        !isNil(property.valueText)
                      ) {
                        return value.some(
                          allowedValue =>
                            allowedValue.value === property.valueText
                        );
                      } else if (
                        property.isText &&
                        property.isArray &&
                        !isNil(property.valueTextArray)
                      ) {
                        return property.valueTextArray.every(valueText =>
                          value.some(
                            allowedValue => allowedValue.value === valueText
                          )
                        );
                      }
                    }
                  }

                  return true;
                },
                message: t("Allowed values must contain current value", {
                  ns: "QuoteItem",
                  value:
                    property && property.isText && property.isArray ? 2 : 1,
                }),
              }),
          }),
      }),
    }),
    [t, property]
  );
};
